import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useDeleteSource,
  useGetSourceByLead,
  useUpsertSource,
} from '@/application/services/source.service'
import {
  useIsPrivilegedForLead,
  useValidateFactScore,
  useGetFactScoreSummary,
} from '@/application/services/fact.service'
import { useAuthStore } from '@/application/stores/auth.store'
import { Button } from '@/ui/components/common/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/common/Dialog'
import { NutriScore } from '@/ui/components/common/NutriScore'
import { FactScoreInline } from '@/ui/components/common/FactScoreInline'
import { ScoreGradeSelector } from '@/ui/components/common/ScoreGradeSelector'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { ImageField } from '@/ui/components/forms/ImageField'
import { Input } from '@/ui/components/forms/Input'
import type { ScoreLetter } from '@/domains/types'
import { toScoreLetter } from '@/domains/types'

const makeSourceSchema = (translate: (key: string, opts?: Record<string, unknown>) => string) =>
  z.object({
    title: z
      .string()
      .min(1, translate('validation.required'))
      .max(200, translate('validation.max_chars', { count: 200 })),
    description: z
      .string()
      .min(1, translate('validation.required'))
      .max(20000, translate('validation.max_chars', { count: 20000 })),
    sourceUrl: z
      .string()
      .min(1, translate('validation.required'))
      .url(translate('validation.url_invalid')),
  })

type SourceFormData = z.infer<ReturnType<typeof makeSourceSchema>>

/**
 * Shared source section for both public and management lead detail pages.
 * Pass `showManagement` on the management page so the score column renders in
 * management mode (both validated + average, no voting widget) — same as FactTile.
 * On the public page (default), score renders in public mode: voting widget when
 * the user is privileged and no validated score has been set yet.
 */
export function LeadSourceSection({
  leadId,
  showManagement = false,
}: {
  leadId: string
  showManagement?: boolean
}) {
  const { t } = useTranslation()
  const sourceSchema = useMemo(() => makeSourceSchema(t), [t])
  const { user } = useAuthStore()
  const { data: source, isLoading } = useGetSourceByLead(leadId)
  const { mutateAsync: upsertSource } = useUpsertSource(leadId)
  const { mutateAsync: deleteSource } = useDeleteSource(leadId)

  const canEdit = user?.role === 'streamer' || user?.role === 'admin'
  const { data: isPrivileged = false } = useIsPrivilegedForLead(leadId)

  // source.id is the underlying fact ID — used for scoring
  const factId = source?.id ?? ''

  const { data: scoreSummary } = useGetFactScoreSummary(leadId, factId, {})
  const { mutateAsync: validateScore } = useValidateFactScore(leadId, factId)

  const [editValidatedScore, setEditValidatedScore] = useState<ScoreLetter | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [imageUrl, setImageUrl] = useState('')

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SourceFormData>({
    resolver: zodResolver(sourceSchema),
  })

  const openDialog = () => {
    reset({
      title: source?.title ?? '',
      description: source?.description ?? '',
      sourceUrl: source?.url ?? '',
    })
    setImageUrl(source?.imageUrl ?? '')
    setEditValidatedScore(toScoreLetter(scoreSummary?.validatedScore))
    setDialogOpen(true)
  }

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      reset()
      setImageUrl('')
      setEditValidatedScore(null)
    }
    setDialogOpen(open)
  }

  const onSubmit = async (data: SourceFormData) => {
    try {
      await upsertSource({
        title: data.title,
        description: data.description,
        sourceUrl: data.sourceUrl || null,
        imageUrl: imageUrl || null,
      })
      if (canEdit && factId) {
        try {
          await validateScore(editValidatedScore)
        } catch {
          toast.error(t('lead_detail.fact_score_validate_error'))
        }
      }
      toast.success(t('lead_detail.source_saved'))
      setDialogOpen(false)
      reset()
      setImageUrl('')
      setEditValidatedScore(null)
    } catch (error) {
      toast.error(
        t('lead_detail.source_save_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(t('lead_detail.source_delete_confirm'))) return
    try {
      await deleteSource()
      toast.success(t('lead_detail.source_deleted'))
    } catch (error) {
      toast.error(
        t('lead_detail.source_delete_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  return (
    <div className="mb-8">
      {(source || canEdit) && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl font-bold">{t('lead_detail.source_title')}</h2>
          {canEdit && (
            <Button
              size="sm"
              onClick={openDialog}
              leftIcon={source ? <Pencil className="w-3.5 h-3.5" /> : <Plus className="h-4 w-4" />}
            >
              {source ? t('lead_detail.source_edit_button') : t('lead_detail.source_add_button')}
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-24 w-full rounded-lg" />
      ) : source ? (
        <div className="bg-card border border-border rounded-lg overflow-hidden flex items-stretch hover:border-primary/50 transition-colors">
          <div className="flex items-stretch flex-1 min-w-0">
            {source.imageUrl && (
              <img
                src={source.imageUrl}
                alt={source.title}
                className="w-40 shrink-0 object-cover"
              />
            )}
            <div className="border-l-4 border-primary p-5 flex-1 min-w-0 flex flex-col gap-3">
              <div>
                <h3 className="font-serif text-lg font-bold mb-1">{source.title}</h3>
                <MarkdownView content={source.description} />
              </div>
              {source.url && (
                <div>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-white/20 bg-white/5 hover:bg-white/10 text-foreground transition-colors no-underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {t('lead_detail.fact_source_button')}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Right: score + actions */}
          <div className="flex items-center gap-3 px-3 py-4 shrink-0 border-l border-border/50">
            <FactScoreInline
              leadId={leadId}
              factId={factId}
              showBoth={showManagement}
              allowVoting={isPrivileged}
            />
            {canEdit && (
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={openDialog}
                  className="p-1.5 rounded-lg bg-background/90 border border-border text-muted-foreground hover:bg-muted/50 transition-all"
                  aria-label={t('lead_detail.source_edit_button')}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  className="p-1.5 rounded-lg bg-background/90 border border-border text-destructive hover:bg-destructive/10 transition-all"
                  aria-label={t('lead_detail.source_delete_confirm')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        canEdit && (
          <p className="text-sm text-muted-foreground">{t('lead_detail.source_no_source')}</p>
        )
      )}

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {source
                ? t('lead_detail.source_dialog_edit_title')
                : t('lead_detail.source_dialog_add_title')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="source-title">
                {t('lead_detail.source_title_label')}
              </label>
              <Input
                id="source-title"
                placeholder={t('lead_detail.source_title_placeholder')}
                {...register('title')}
              />
              {errors.title && <p className="text-destructive text-xs">{errors.title.message}</p>}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="source-description">
                {t('lead_detail.source_description_label')}
              </label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('lead_detail.source_description_placeholder')}
                    onUploadError={(message) => toast.error(message)}
                  />
                )}
              />
              {errors.description && (
                <p className="text-destructive text-xs">{errors.description.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="source-url">
                {t('lead_detail.source_url_label')}
              </label>
              <Input
                id="source-url"
                type="url"
                placeholder={t('lead_detail.source_url_placeholder')}
                {...register('sourceUrl')}
              />
              {errors.sourceUrl && (
                <p className="text-destructive text-xs">{errors.sourceUrl.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('lead_detail.source_image_label')}</label>
              <ImageField value={imageUrl} onChange={setImageUrl} />
            </div>

            {canEdit && source && (
              <>
                {scoreSummary?.averageScore && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('lead_detail.fact_score_average_label')}
                      {scoreSummary.count > 0 && (
                        <span className="font-normal ml-1">
                          (
                          {t('lead_detail.fact_score_voters', {
                            count: scoreSummary.count,
                          })}
                          )
                        </span>
                      )}
                    </label>
                    <NutriScore grade={scoreSummary.averageScore} size="sm" showLabel={false} />
                  </div>
                )}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    {t('lead_detail.fact_score_validated_label')}
                  </label>
                  <ScoreGradeSelector
                    value={editValidatedScore}
                    onChange={setEditValidatedScore}
                    disabled={isSubmitting}
                  />
                </div>
              </>
            )}

            <DialogFooter className="pt-2">
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={isSubmitting}>
                  {t('common.cancel')}
                </Button>
              </DialogClose>
              <Button type="submit" isLoading={isSubmitting}>
                {source ? t('lead_detail.source_submit_edit') : t('lead_detail.source_submit_add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
